import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Button, Typography } from "@mui/material";
import Pagination from "@mui/material/Pagination";
import { pageScrollStyle } from "../styles/styles";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

export default function DataTable({
  rows,
  columns,
  page,
  setPage,
  pageSize,
  rowCount,
  CustomToolbar,
  CustomNoRowsOverlay,
  height,
  loading,
  checkboxSelection,
  onSelectionChange,
  onPageChange,
  onPageSizeChange,
  hasNextPage,
  hasPreviousPage,
  handlePageChange,
}) {
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(page || 0);

  // Update internal state when props change
  useEffect(() => {
    setCurrentPage(page);
  }, [page]);

  const handleSelectionChange = (ids) => {
    const selectedRowsData = rows.filter((row) => ids.includes(row.id));
    setSelectedRows(selectedRowsData);
    if (onSelectionChange) {
      onSelectionChange(selectedRowsData);
    }
  };

  const totalPages = Math.ceil(rowCount / pageSize);

  const handlePaginationChange = (event, value) => {
    const newPage = value; // Remove the subtraction to keep page one-based
    setPage(newPage);
    setCurrentPage(newPage);
    if (handlePageChange) {
      handlePageChange(newPage);
    } else if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const loadingOverlayProps = {
    variant: "skeleton",
    noRowsVariant: "skeleton",
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          height: height || 400,
          width: "100%",
          "& .MuiDataGrid-root": {
            overflowX: "auto",
          },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          pageSize={pageSize}
          checkboxSelection={checkboxSelection}
          onRowSelectionModelChange={handleSelectionChange}
          disableRowSelectionOnClick
          disableSelectionOnClick
          columnVisibilityModel={{}}
          disableColumnResize={false}
          loading={loading}
          hideFooter={false}
          hideFooterPagination={true}
          initialState={{
            pinnedColumns: {
              left: ["desk"],
            },
            density: "compact",
          }}
          slotProps={{
            loadingOverlay: loadingOverlayProps,
          }}
          slots={{
            ...(CustomToolbar && {
              toolbar: () => <CustomToolbar selectedRows={selectedRows} />,
            }),
            ...(CustomNoRowsOverlay && {
              noRowsOverlay: CustomNoRowsOverlay,
            }),
            // Custom footer with pagination
            footer: () => (
              <Box
                sx={{
                  p: 1,
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ fontSize: "0.8rem" }}
                  >
                    {`${(currentPage - 1) * pageSize + 1}-${Math.min(
                      currentPage * pageSize,
                      rowCount
                    )} of ${rowCount}`}
                  </Typography>
                </Box>
                <Pagination
                  count={totalPages}
                  variant="outlined"
                  color="primary"
                  page={currentPage}
                  onChange={handlePaginationChange}
                  disabled={loading}
                  size="small"
                />
              </Box>
            ),
          }}
          sx={{
            ...pageScrollStyle,
            border: "1px solid #e5e5e5",
            ".MuiDataGrid-cell": {
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              borderBottom: "1px solid #e5e5e5",
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#00b3b02c",
            },
            "& .MuiDataGrid-columnSeparator": {
              display: "none",
            },
            "& .MuiDataGrid-main": {
              overflow: "auto",
              scrollbarWidth: "thin",
              scrollBehavior: "smooth",
            },
            "& .MuiDataGrid-virtualScroller": {
              overflow: "auto",
              scrollBehavior: "smooth",
            },
          }}
        />
      </Box>
    </Box>
  );
}
